import React, { useState, useEffect, useRef, useCallback } from 'react';
import { io } from 'socket.io-client';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Textarea } from './ui/textarea';
import { 
  Navigation, MapPin, Radio, Square, Play, Loader2,
  Truck, Shield, Ship, AlertTriangle, MessageSquare
} from 'lucide-react';
import { API_BASE_URL as CONFIG_BASE_URL } from '../config';

// Use the same runtime-resolved URL as the main app (config.js uses window.location.origin in deployment)
const API_BASE_URL = (CONFIG_BASE_URL || '').replace(/\/api\/?$/, '');

const STATUS_OPTIONS = [
  { value: 'Assigned', color: '#3b82f6', label: 'Assigned' },
  { value: 'En Route', color: '#f59e0b', label: 'En Route' },
  { value: 'On Scene', color: '#22c55e', label: 'On Scene' },
  { value: 'Available', color: '#8b5cf6', label: 'Available' },
  { value: 'Out of Service', color: '#ef4444', label: 'Out of Service' },
];

const VEHICLE_ICON_EMOJIS = {
  fire_truck: '\u{1F692}', ambulance: '\u{1F691}', command: '\u{1F4E1}',
  heavy_equipment: '\u{1F6DC}', water_tender: '\u{1F4A7}', rescue: '\u26D1',
  hazmat: '\u2622', helicopter: '\u{1F681}', boat: '\u26F5',
  utility: '\u{1F527}', transport: '\u{1F68C}', police: '\u{1F6A8}',
};

const ResourceTrackerPage = () => {
  // Extract tracking code from hash: #tracker/ABC123 or #/tracker/ABC123
  const rawHash = window.location.hash.substring(1).replace(/^\//, ''); // strip leading /
  const hashParts = rawHash.split('/');
  const trackingCode = (hashParts.length > 1 ? hashParts[1] : '').trim();

  const [phase, setPhase] = useState(trackingCode ? 'validating' : 'code-entry');
  const [codeInput, setCodeInput] = useState(trackingCode);
  const [error, setError] = useState('');
  const [resourceData, setResourceData] = useState(null);
  const [currentStatus, setCurrentStatus] = useState('Assigned');
  const [notes, setNotes] = useState('');

  // Tracking state
  const [isTracking, setIsTracking] = useState(false);
  const [position, setPosition] = useState({ lat: null, lng: null });
  const [speed, setSpeed] = useState(0);
  const [heading, setHeading] = useState(0);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [gpsAccuracy, setGpsAccuracy] = useState(null);
  const [socketConnected, setSocketConnected] = useState(false);
  const [updateCount, setUpdateCount] = useState(0);
  const [lastServerStatus, setLastServerStatus] = useState(null);

  const socketRef = useRef(null);
  const watchIdRef = useRef(null);
  const trackerIdRef = useRef('');
  const restIntervalRef = useRef(null);
  const gpsIntervalRef = useRef(null);
  const lastPositionRef = useRef(null);
  // Ref to always hold the latest status — avoids stale closure in watchPosition GPS callback
  const currentStatusRef = useRef(currentStatus);

  // Validate code on mount or when user submits
  useEffect(() => {
    if (trackingCode) {
      validateCode(trackingCode);
    }
  }, []);

  const validateCode = async (code) => {
    setPhase('validating');
    setError('');
    const upperCode = code.toUpperCase();
    // Try primary URL first, then fall back to origin-relative URL
    const urls = [
      `${API_BASE_URL}/api/tracker/validate/${upperCode}`,
      `${window.location.origin}/api/tracker/validate/${upperCode}`,
    ];
    // Deduplicate (they may be the same)
    const uniqueUrls = [...new Set(urls.filter(Boolean))];

    for (let attempt = 0; attempt < 2; attempt++) {
      for (const url of uniqueUrls) {
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 10000);
          try {
            const res = await fetch(url, { signal: controller.signal });
            clearTimeout(timeoutId);
            if (res.ok) {
              const data = await res.json();
              setResourceData(data);
              setCurrentStatus(data.status || 'Assigned');
              currentStatusRef.current = data.status || 'Assigned';
              setPhase('ready');
              return;
            }
            // Non-OK response — parse error
            const contentType = res.headers.get('content-type') || '';
            if (contentType.includes('application/json')) {
              const err = await res.json().catch(() => ({}));
              const detail = err.detail || '';
              // Map raw backend messages to user-friendly text
              if (detail === 'Not Found') {
                // Route mismatch — likely URL config issue; try next URL
                console.warn(`[Tracker] Route not found at ${url}`);
                continue;
              }
              setError(detail || 'Invalid tracking code. Please check and try again.');
            } else {
              setError('Invalid tracking code. Please check and try again.');
            }
            setPhase('code-entry');
            return;
          } catch (e) {
            clearTimeout(timeoutId);
            console.warn(`[Tracker] Validate attempt ${attempt + 1} failed for ${url}:`, e.message);
            // Continue to next URL or retry
          }
      }
      // Brief delay before retry
      if (attempt === 0) await new Promise(r => setTimeout(r, 1500));
    }
    setError('Unable to connect to the server. Please try again in a moment.');
    setPhase('code-entry');
  };

  const handleCodeSubmit = (e) => {
    e.preventDefault();
    if (codeInput.trim().length < 4) {
      setError('Please enter a valid tracking code.');
      return;
    }
    validateCode(codeInput.trim());
  };

  // Keep the ref in sync with the latest status state
  useEffect(() => {
    currentStatusRef.current = currentStatus;
  }, [currentStatus]);

  // GPS position callback — sends via socket AND REST fallback
  const onPositionUpdate = useCallback((pos) => {
    const { latitude, longitude, speed: spd, heading: hdg, accuracy } = pos.coords;
    setPosition({ lat: latitude, lng: longitude });
    setSpeed(spd || 0);
    setHeading(hdg || 0);
    setGpsAccuracy(accuracy);
    setLastUpdate(new Date());

    const payload = {
      exercise_id: resourceData?.assigned_exercise || '',
      tracker_id: trackerIdRef.current,
      lat: latitude,
      lng: longitude,
      speed: spd || 0,
      heading: hdg || 0,
      status: currentStatusRef.current,
      callsign: resourceData?.callsign || '',
      identification: resourceData?.identification || '',
      resource_type: resourceData?.resource_type || '',
      vehicle_icon: resourceData?.vehicle_icon || 'fire_truck',
    };

    // Store last position for periodic re-send
    lastPositionRef.current = payload;

    // Try socket first
    if (socketRef.current?.connected && trackerIdRef.current) {
      socketRef.current.emit('position_update', payload);
    }

    // Always send via REST as backup (ensures data reaches active_trackers dict)
    if (trackerIdRef.current) {
      const baseUrl = API_BASE_URL || window.location.origin;
      fetch(`${baseUrl}/api/tracker/position`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      }).then(res => {
        setUpdateCount(c => c + 1);
        setLastServerStatus(res.ok ? 'ok' : `error:${res.status}`);
      }).catch(err => {
        setLastServerStatus(`fail:${err.message}`);
      });
    }
  }, [resourceData]);

  const startTracking = async () => {
    if (!resourceData) return;

    // Check Geolocation support
    if (!navigator.geolocation) {
      setError('Geolocation is not supported by this browser.');
      return;
    }

    setError('');
    const trackerId = `tracker-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    trackerIdRef.current = trackerId;
    const baseUrl = (API_BASE_URL || window.location.origin).replace(/\/+$/, '');

    const trackerPayload = {
      exercise_id: resourceData.assigned_exercise,
      tracker_id: trackerId,
      callsign: resourceData.callsign,
      identification: resourceData.identification,
      resource_type: resourceData.resource_type,
      vehicle_icon: resourceData.vehicle_icon || 'fire_truck',
      vehicle_equipment_make_model: resourceData.vehicle_equipment_make_model,
      status: currentStatus,
      task_force: resourceData.task_force,
      strike_team: resourceData.strike_team,
      contact_person: resourceData.contact_person,
      contact_phone: resourceData.contact_phone,
    };

    // Register tracker via REST (primary — works regardless of socket.IO)
    try {
      await fetch(`${baseUrl}/api/tracker/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(trackerPayload),
      });
      console.log('[Tracker] Registered via REST');
    } catch (e) {
      console.warn('[Tracker] REST register failed:', e.message);
    }

    // Notify backend to find-or-create resource in Resource Management
    try {
      await fetch(`${baseUrl}/api/tracker/start-by-callsign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(trackerPayload),
      });
    } catch {
      // Non-blocking
    }

    // Connect socket (enhancement — provides real-time events to map viewer)
    const socket = io(baseUrl, {
      path: '/api/ws/socket.io',
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 2000,
      reconnectionAttempts: 5,
    });
    socketRef.current = socket;

    socket.on('connect', () => {
      console.log('[Tracker] Socket connected:', socket.id);
      setSocketConnected(true);
      socket.emit('start_tracking', trackerPayload);
    });

    socket.on('disconnect', () => {
      console.log('[Tracker] Socket disconnected');
      setSocketConnected(false);
    });

    socket.on('connect_error', (err) => {
      console.warn('[Tracker] Socket connection error:', err.message);
      setSocketConnected(false);
    });

    // Start GPS watch
    watchIdRef.current = navigator.geolocation.watchPosition(
      onPositionUpdate,
      (err) => setError(`GPS Error: ${err.message}`),
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 15000 }
    );

    // Periodic GPS re-send every 30 seconds — requests FRESH position and sends to server
    gpsIntervalRef.current = setInterval(() => {
      if (!trackerIdRef.current) return;
      const baseUrl = API_BASE_URL || window.location.origin;

      // Request fresh GPS position
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const { latitude, longitude, speed: spd, heading: hdg, accuracy } = pos.coords;
          setPosition({ lat: latitude, lng: longitude });
          setSpeed(spd || 0);
          setHeading(hdg || 0);
          setGpsAccuracy(accuracy);
          setLastUpdate(new Date());

          const payload = {
            exercise_id: resourceData?.assigned_exercise || '',
            tracker_id: trackerIdRef.current,
            lat: latitude,
            lng: longitude,
            speed: spd || 0,
            heading: hdg || 0,
            status: currentStatusRef.current,
            callsign: resourceData?.callsign || '',
            identification: resourceData?.identification || '',
            resource_type: resourceData?.resource_type || '',
            vehicle_icon: resourceData?.vehicle_icon || 'fire_truck',
          };
          lastPositionRef.current = payload;

          fetch(`${baseUrl}/api/tracker/position`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          }).then(res => {
            setUpdateCount(c => c + 1);
            setLastServerStatus(res.ok ? 'ok' : `error:${res.status}`);
          }).catch(err => {
            setLastServerStatus(`fail:${err.message}`);
          });
        },
        () => {
          // getCurrentPosition failed — fall back to last known position
          if (lastPositionRef.current) {
            const payload = { ...lastPositionRef.current, status: currentStatusRef.current };
            fetch(`${baseUrl}/api/tracker/position`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(payload),
            }).then(res => {
              setUpdateCount(c => c + 1);
              setLastServerStatus(res.ok ? 'ok (cached)' : `error:${res.status}`);
            }).catch(err => {
              setLastServerStatus(`fail:${err.message}`);
            });
          }
        },
        { enableHighAccuracy: true, maximumAge: 10000, timeout: 10000 }
      );
    }, 30000);

    setIsTracking(true);
    setPhase('tracking');
  };

  const stopTracking = () => {
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    if (gpsIntervalRef.current) {
      clearInterval(gpsIntervalRef.current);
      gpsIntervalRef.current = null;
    }
    lastPositionRef.current = null;
    if (socketRef.current) {
      socketRef.current.emit('stop_tracking', {
        exercise_id: resourceData?.assigned_exercise || '',
        tracker_id: trackerIdRef.current,
      });
      socketRef.current.disconnect();
      socketRef.current = null;
    }
    // Unregister via REST
    const baseUrl = (API_BASE_URL || window.location.origin).replace(/\/+$/, '');
    fetch(`${baseUrl}/api/tracker/unregister`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        exercise_id: resourceData?.assigned_exercise || '',
        tracker_id: trackerIdRef.current,
      }),
    }).catch(() => {});
    setSocketConnected(false);
    setIsTracking(false);
    setPhase('ready');
  };

  // Update status on backend and via socket
  const handleStatusChange = async (newStatus) => {
    setCurrentStatus(newStatus);
    const baseUrl = (API_BASE_URL || window.location.origin).replace(/\/+$/, '');
    
    // Update resource in DB
    if (resourceData?.tracking_code) {
      try {
        await fetch(`${baseUrl}/api/tracker/update-status/${resourceData.tracking_code}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ status: newStatus }),
        });
      } catch {}
    }

    // Broadcast via socket if connected
    if (socketRef.current?.connected && trackerIdRef.current && position.lat) {
      socketRef.current.emit('position_update', {
        exercise_id: resourceData?.assigned_exercise || '',
        tracker_id: trackerIdRef.current,
        lat: position.lat,
        lng: position.lng,
        speed,
        heading,
        status: newStatus,
      });
    }

    // Also update via REST fallback
    if (trackerIdRef.current && position.lat) {
      fetch(`${baseUrl}/api/tracker/position`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          exercise_id: resourceData?.assigned_exercise || '',
          tracker_id: trackerIdRef.current,
          lat: position.lat,
          lng: position.lng,
          speed,
          heading,
          status: newStatus,
          callsign: resourceData?.callsign || '',
          resource_type: resourceData?.resource_type || '',
          vehicle_icon: resourceData?.vehicle_icon || 'fire_truck',
        }),
      }).catch(() => {});
    }
  };

  const handleNotesSubmit = async () => {
    if (resourceData?.tracking_code && notes.trim()) {
      try {
        await fetch(`${API_BASE_URL}/api/tracker/update-status/${resourceData.tracking_code}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ notes: notes.trim() }),
        });
      } catch {}
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (watchIdRef.current !== null) navigator.geolocation.clearWatch(watchIdRef.current);
      if (socketRef.current) socketRef.current.disconnect();
    };
  }, []);

  const vehicleEmoji = VEHICLE_ICON_EMOJIS[resourceData?.vehicle_icon] || '\u{1F4CD}';
  const statusColor = STATUS_OPTIONS.find(s => s.value === currentStatus)?.color || '#6b7280';

  // ─── Code Entry Screen ───
  if (phase === 'code-entry' || phase === 'validating') {
    return (
      <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
        <Card className="w-full max-w-sm bg-gray-800 border-gray-700">
          <CardHeader className="text-center pb-2">
            <div style={{ fontSize: '48px', marginBottom: '8px' }}>{'\u{1F4E1}'}</div>
            <CardTitle className="text-white text-xl">EXRSIM Resource Tracker</CardTitle>
            <p className="text-gray-400 text-sm mt-1">Enter your tracking code to begin</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleCodeSubmit} className="space-y-4">
              <div>
                <Label className="text-gray-300 text-sm">Tracking Code</Label>
                <Input
                  value={codeInput}
                  onChange={(e) => setCodeInput(e.target.value.toUpperCase())}
                  placeholder="e.g. ABC123"
                  className="bg-gray-700 border-gray-600 text-white text-center text-2xl tracking-[0.3em] font-mono mt-1"
                  maxLength={8}
                  autoFocus
                  disabled={phase === 'validating'}
                  data-testid="tracker-code-input"
                />
              </div>
              {error && (
                <div className="bg-red-900/50 border border-red-700 text-red-300 px-3 py-2 rounded text-sm" data-testid="tracker-error">
                  {error}
                </div>
              )}
              <Button
                type="submit"
                className="w-full bg-orange-600 hover:bg-orange-700 text-white"
                disabled={phase === 'validating' || codeInput.trim().length < 4}
                data-testid="tracker-code-submit"
              >
                {phase === 'validating' ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Validating...</>
                ) : (
                  <><Radio className="h-4 w-4 mr-2" /> Connect</>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ─── Ready / Tracking Screen ───
  return (
    <div style={{ minHeight: '100vh', background: '#0f172a', color: '#fff' }}>
      {/* Header */}
      <div style={{ background: isTracking ? statusColor : '#1e293b', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: '12px', transition: 'background 0.3s' }}>
        <span style={{ fontSize: '28px' }}>{vehicleEmoji}</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: '18px' }}>{resourceData?.callsign || 'Unknown'}</div>
          <div style={{ fontSize: '12px', opacity: 0.8 }}>{resourceData?.resource_type} {resourceData?.vehicle_equipment_make_model ? `\u2022 ${resourceData.vehicle_equipment_make_model}` : ''}</div>
        </div>
        {isTracking && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: socketConnected ? '#22c55e' : '#f59e0b', animation: 'pulse 1.5s infinite' }} />
            <span style={{ fontSize: '12px', fontWeight: 600 }}>{socketConnected ? 'LIVE' : 'REST'}</span>
          </div>
        )}
      </div>

      <div style={{ padding: '16px', maxWidth: '480px', margin: '0 auto' }}>
        {/* Resource Details Card */}
        <Card className="bg-gray-800 border-gray-700 mb-4">
          <CardContent className="pt-4">
            <div className="grid grid-cols-2 gap-2 text-sm">
              {resourceData?.identification && (
                <div><span className="text-gray-400">ID: </span><span className="text-white">{resourceData.identification}</span></div>
              )}
              {resourceData?.task_force && (
                <div><span className="text-gray-400">TF: </span><span className="text-white">{resourceData.task_force}</span></div>
              )}
              {resourceData?.strike_team && (
                <div><span className="text-gray-400">ST: </span><span className="text-white">{resourceData.strike_team}</span></div>
              )}
              {resourceData?.contact_person && (
                <div><span className="text-gray-400">Contact: </span><span className="text-white">{resourceData.contact_person}</span></div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Status Selector */}
        <div className="mb-4">
          <Label className="text-gray-400 text-xs mb-2 block">STATUS</Label>
          <div className="grid grid-cols-3 gap-2">
            {STATUS_OPTIONS.map(opt => (
              <button
                key={opt.value}
                onClick={() => handleStatusChange(opt.value)}
                disabled={!isTracking}
                data-testid={`status-${opt.value.toLowerCase().replace(/\s/g, '-')}`}
                style={{
                  background: currentStatus === opt.value ? opt.color : 'transparent',
                  border: `2px solid ${opt.color}`,
                  color: currentStatus === opt.value ? '#fff' : opt.color,
                  borderRadius: '8px',
                  padding: '8px 4px',
                  fontSize: '12px',
                  fontWeight: 600,
                  cursor: isTracking ? 'pointer' : 'not-allowed',
                  opacity: isTracking ? 1 : 0.5,
                  transition: 'all 0.2s',
                }}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Notes */}
        {isTracking && (
          <div className="mb-4">
            <Label className="text-gray-400 text-xs mb-1 block">NOTES</Label>
            <div className="flex gap-2">
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add a note..."
                className="bg-gray-700 border-gray-600 text-white text-sm resize-none"
                rows={2}
                data-testid="tracker-notes"
              />
              <Button
                onClick={handleNotesSubmit}
                className="bg-gray-600 hover:bg-gray-500 self-end"
                size="sm"
                disabled={!notes.trim()}
                data-testid="tracker-notes-submit"
              >
                <MessageSquare className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {/* GPS Info */}
        {isTracking && position.lat && (
          <Card className="bg-gray-800/50 border-gray-700 mb-4">
            <CardContent className="pt-3 pb-3">
              <div className="grid grid-cols-2 gap-y-1 text-xs">
                <div><span className="text-gray-500">Lat: </span><span className="text-gray-300 font-mono">{position.lat.toFixed(6)}</span></div>
                <div><span className="text-gray-500">Lng: </span><span className="text-gray-300 font-mono">{position.lng.toFixed(6)}</span></div>
                <div><span className="text-gray-500">Speed: </span><span className="text-gray-300 font-mono">{(speed * 3.6).toFixed(1)} km/h</span></div>
                <div><span className="text-gray-500">Heading: </span><span className="text-gray-300 font-mono">{heading ? `${Math.round(heading)}\u00B0` : 'N/A'}</span></div>
                {gpsAccuracy && <div className="col-span-2"><span className="text-gray-500">Accuracy: </span><span className="text-gray-300 font-mono">{'\u00B1'}{Math.round(gpsAccuracy)}m</span></div>}
                {lastUpdate && <div className="col-span-2"><span className="text-gray-500">GPS Update: </span><span className="text-gray-300">{lastUpdate.toLocaleTimeString()}</span></div>}
                <div className="col-span-2 flex items-center gap-3">
                  <span className="text-gray-500">Server: </span>
                  <span className={`font-mono text-xs ${lastServerStatus === 'ok' ? 'text-green-400' : lastServerStatus?.startsWith('ok') ? 'text-yellow-400' : lastServerStatus ? 'text-red-400' : 'text-gray-500'}`} data-testid="server-status">
                    {lastServerStatus || 'waiting...'}
                  </span>
                  <span className="text-gray-500 text-xs" data-testid="update-count">({updateCount} sent)</span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {error && (
          <div className="bg-red-900/50 border border-red-700 text-red-300 px-3 py-2 rounded text-sm mb-4" data-testid="tracker-error">
            <AlertTriangle className="h-4 w-4 inline mr-1" /> {error}
          </div>
        )}

        {/* Start / Stop Button */}
        {!isTracking ? (
          <Button
            onClick={startTracking}
            className="w-full bg-green-600 hover:bg-green-700 text-white py-6 text-lg"
            data-testid="tracker-start-btn"
          >
            <Play className="h-5 w-5 mr-2" /> Start Tracking
          </Button>
        ) : (
          <Button
            onClick={stopTracking}
            className="w-full bg-red-600 hover:bg-red-700 text-white py-6 text-lg"
            data-testid="tracker-stop-btn"
          >
            <Square className="h-5 w-5 mr-2" /> Stop Tracking
          </Button>
        )}
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
      `}</style>
    </div>
  );
};

export default ResourceTrackerPage;
